import { isLeft, isRight } from 'fp-ts/Either';
import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { DappReviewSummaryRunDetail } from '../components/aiSummaries/DappReviewSummaryRunDetail';
import { DappReviewSummaryRunsTable } from '../components/aiSummaries/DappReviewSummaryRunsTable';
import { Button } from '../components/aiSummaries/uiButton';
import { DappReviewSummarySettingsPanel } from '../components/DappReviewSummarySettingsPanel';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { TRPCContext } from '../contexts/TRPC';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRequireAuth } from '../hooks/useRequireAuth';
import type { RightResult, RouterOutputs } from '../types/TRPC';

type RunsListResult = RightResult<RouterOutputs['listDappReviewSummaryRuns']>;
type RunListItem = RunsListResult['runs'][number];
type RunDetailResult = RightResult<
  RouterOutputs['getDappReviewSummaryRunDetail']
>;
type PublisherEmailPreviewResult = RightResult<
  RouterOutputs['getDappReviewSummaryRunPublisherEmailPreview']
>;

const DEFAULT_PAGE_SIZE = 25;

export function AiSummariesPage() {
  const { canRender } = useRequireAuth({ requireRole: 'admin' });
  usePageTitle('AI Summaries | Publishing Portal');
  const trpc = use(TRPCContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRunId = searchParams.get('run');

  const [page, setPage] = useState(0);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [runs, setRuns] = useState<RunListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [runDetail, setRunDetail] = useState<RunDetailResult | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [retryingFailedRun, setRetryingFailedRun] = useState(false);
  const [sendingPublisherEmails, setSendingPublisherEmails] = useState(false);
  const [loadingPublisherEmailPreview, setLoadingPublisherEmailPreview] =
    useState(false);
  const [publisherEmailPreview, setPublisherEmailPreview] =
    useState<PublisherEmailPreviewResult | null>(null);

  const loadRuns = useCallback(async () => {
    if (!trpc?.client) {
      return;
    }

    try {
      setLoadingRuns(true);
      const result = await trpc.client.listDappReviewSummaryRuns.query({
        limit: pageSize,
        offset: page * pageSize,
      });

      if (isRight(result)) {
        setRuns(result.right.runs);
        setTotal(result.right.total);
      } else {
        toast.error(result.left.message);
      }
    } catch {
      toast.error('Failed to load AI summary runs');
    } finally {
      setLoadingRuns(false);
    }
  }, [trpc, page, pageSize]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const loadDetail = useCallback(
    async (runId: string) => {
      if (!trpc?.client) {
        return;
      }

      try {
        setLoadingDetail(true);
        const result = await trpc.client.getDappReviewSummaryRunDetail.query({
          runId,
        });

        if (isRight(result)) {
          setRunDetail(result.right);
        } else {
          toast.error(result.left.message);
          setRunDetail(null);
        }
      } catch {
        toast.error('Failed to load run detail');
        setRunDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    },
    [trpc],
  );

  useEffect(() => {
    if (!selectedRunId) {
      setRunDetail(null);
      return;
    }
    void loadDetail(selectedRunId);
  }, [selectedRunId, loadDetail]);

  const handleSelectRun = (runId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('run', runId);
    setSearchParams(next, { replace: false });
  };

  const handleClearSelection = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('run');
    setSearchParams(next, { replace: false });
  };

  const handleRetryFailedRun = useCallback(
    async (runId: string) => {
      if (!trpc?.client) {
        return;
      }

      try {
        setRetryingFailedRun(true);
        const result = await trpc.client.retryFailedDappReviewSummaryRun.mutate(
          {
            runId,
          },
        );

        if (isRight(result)) {
          const failedCount = result.right.androidPackages.length;
          toast.success(
            `Retry started for ${failedCount} failed app${
              failedCount === 1 ? '' : 's'
            }`,
          );
          await loadDetail(runId);
          void loadRuns();
          window.setTimeout(() => void loadDetail(runId), 3000);
        } else if (isLeft(result)) {
          toast.error(result.left.message);
        }
      } catch {
        toast.error('Failed to retry failed apps');
      } finally {
        setRetryingFailedRun(false);
      }
    },
    [trpc, loadDetail, loadRuns],
  );

  const handleSendPublisherEmails = useCallback(
    async (runId: string) => {
      if (!trpc?.client) {
        return;
      }

      try {
        setLoadingPublisherEmailPreview(true);
        const preview =
          await trpc.client.getDappReviewSummaryRunPublisherEmailPreview.query({
            runId,
          });

        if (isLeft(preview)) {
          toast.error(preview.left.message);
          return;
        }

        if (preview.right.recipients.length === 0) {
          toast.error(
            'No unsent completed publisher summary emails are eligible to send.',
          );
          await loadDetail(runId);
          void loadRuns();
          return;
        }

        setPublisherEmailPreview(preview.right);
      } catch {
        toast.error('Failed to load publisher summary email preview');
      } finally {
        setLoadingPublisherEmailPreview(false);
      }
    },
    [trpc, loadDetail, loadRuns],
  );

  const handleConfirmPublisherEmails = useCallback(async () => {
    if (!trpc?.client || !publisherEmailPreview) {
      return;
    }

    const preview = publisherEmailPreview;

    try {
      setSendingPublisherEmails(true);
      const result =
        await trpc.client.sendDappReviewSummaryRunPublisherEmails.mutate({
          runId: preview.runId,
          summaryIds: preview.recipients.map(
            (recipient) => recipient.summaryId,
          ),
          confirmPublisherEmail: true,
        });

      if (isRight(result)) {
        toast.success(
          `Sent ${result.right.sentCount} publisher summary ${
            result.right.sentCount === 1 ? 'email' : 'emails'
          }`,
        );
        setPublisherEmailPreview(null);
        await loadDetail(preview.runId);
        void loadRuns();
      } else {
        toast.error(result.left.message);
        await loadDetail(preview.runId);
        void loadRuns();
      }
    } catch {
      toast.error('Failed to send publisher summary emails');
    } finally {
      setSendingPublisherEmails(false);
    }
  }, [trpc, publisherEmailPreview, loadDetail, loadRuns]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize],
  );

  if (!canRender) {
    return null;
  }

  if (selectedRunId) {
    return (
      <div className="flex-1 overflow-y-auto px-4 py-8 md:px-10">
        <div className="mx-auto max-w-6xl">
          <DappReviewSummaryRunDetail
            runId={selectedRunId}
            detail={runDetail}
            loading={loadingDetail}
            retryingFailed={retryingFailedRun}
            sendingPublisherEmails={
              sendingPublisherEmails || loadingPublisherEmailPreview
            }
            onBack={handleClearSelection}
            onRefresh={() => void loadDetail(selectedRunId)}
            onRetryFailed={() => void handleRetryFailedRun(selectedRunId)}
            onSendPublisherEmails={() =>
              void handleSendPublisherEmails(selectedRunId)
            }
          />
          <PublisherEmailConfirmationDialog
            preview={publisherEmailPreview}
            sending={sendingPublisherEmails}
            onOpenChange={(open) => {
              if (!open && !sendingPublisherEmails) {
                setPublisherEmailPreview(null);
              }
            }}
            onConfirm={handleConfirmPublisherEmails}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-8 md:px-10">
      <div className="mx-auto max-w-6xl space-y-10">
        <PageIntro />

        <DappReviewSummarySettingsPanel />

        <DappReviewSummaryRunsTable
          runs={runs}
          loading={loadingRuns}
          total={total}
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onSelect={handleSelectRun}
          onPageChange={setPage}
          onRefresh={() => void loadRuns()}
        />
      </div>
    </div>
  );
}

function PublisherEmailConfirmationDialog({
  preview,
  sending,
  onOpenChange,
  onConfirm,
}: {
  preview: PublisherEmailPreviewResult | null;
  sending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const recipients = preview?.recipients ?? [];

  return (
    <Dialog open={Boolean(preview)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden border-neutral-800 bg-neutral-950 text-white">
        <DialogHeader>
          <DialogTitle>Send publisher summary emails?</DialogTitle>
          <DialogDescription>
            This will send AI review summaries only for completed summaries that
            have not already been sent. Already-sent summaries will not be sent
            again.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4 overflow-hidden py-0">
          <div className="rounded-lg border border-amber-400/20 bg-amber-400/10 px-3.5 py-2.5 text-[13px] leading-relaxed text-amber-100">
            {recipients.length} publisher summary{' '}
            {recipients.length === 1 ? 'email' : 'emails'} will be sent.
          </div>

          <div className="max-h-[52vh] overflow-y-auto rounded-lg border border-white/[0.08]">
            <table className="min-w-full divide-y divide-white/[0.06] text-left text-[12px]">
              <thead className="sticky top-0 bg-neutral-950 text-[11px] uppercase tracking-[0.12em] text-neutral-500">
                <tr>
                  <th className="px-3 py-2 font-medium">App</th>
                  <th className="px-3 py-2 font-medium">Publisher</th>
                  <th className="px-3 py-2 font-medium">
                    Publisher account email
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {recipients.map((recipient) => (
                  <tr key={recipient.summaryId}>
                    <td className="p-3 align-top">
                      <div className="font-medium text-white">
                        {recipient.dappName ?? recipient.androidPackage}
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] text-neutral-500">
                        {recipient.androidPackage}
                      </div>
                    </td>
                    <td className="p-3 align-top text-neutral-200">
                      {recipient.publisherName ?? 'Publisher'}
                    </td>
                    <td className="p-3 align-top font-mono text-[11px] text-neutral-300">
                      {recipient.publisherEmail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogBody>

        <DialogFooter className="gap-2 pt-2">
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            submitting={sending}
            disabled={recipients.length === 0}
          >
            Send {recipients.length}{' '}
            {recipients.length === 1 ? 'email' : 'emails'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PageIntro() {
  return (
    <h1 className="text-2xl font-semibold tracking-tight text-white md:text-[28px]">
      AI summaries
    </h1>
  );
}
